import {
	IOptionTemplateValidation,
	IQuestionnaireTemplateStateData,
	IQuestionnaireTemplateRequest,
	IQuestionnaireTemplateServiceParams,
	IQuestionnaireTemplateValidation,
	IQuestionTemplateRequest,
	IQuestionTemplateValidation,
	ISectionTemplateStateData,
	ISectionTemplateRequest,
	ISectionTemplateValidation,
	IQuestionnaireTemplateResponse,
	IQuestionData,
	IOptionData,
} from 'src/features/questionnaire_template/domain/types';

export const FE_PREFIX = 'AUTO_FE';

export const generateUniqueId = (entityPrefix = 'id') =>
	`${FE_PREFIX}_${entityPrefix}_${Math.random().toString(36).slice(2, 11)}`;

export const isFrontendId = (id: string | number): boolean =>
	typeof id === 'string' && id.startsWith(FE_PREFIX);

export function generateNextHiddenSectionName(
	sections: Record<number | string, ISectionTemplateStateData>,
): string {
	const hiddenCount = Object.values(sections).filter(
		(section) => section.is_hidden === true,
	).length;

	return `main_${hiddenCount + 1}`;
}

export const transformStateStructureToAPIRequest = (
	stateData: IQuestionnaireTemplateStateData,
): IQuestionnaireTemplateRequest => {
	const sections = Object.entries(stateData.sections).map(
		([sectionId, section]) => {
			const sectionData: ISectionTemplateRequest = {
				name: section.name,
				display_name: section.display_name?.trim(),
				is_updated: !!section.is_updated,
				is_deleted: !!section.is_deleted,
				questions: Object.entries(section.questions).map(
					([questionId, question]) => {
						const questionData: IQuestionTemplateRequest = {
							text: question.text?.trim(),
							type: question.type,
							serial_number: question.serial_number,
							is_required: question.is_required,
							is_updated: !!question.is_updated,
							is_deleted: !!question.is_deleted,
							options:
								question.options?.map((option) => ({
									...(option.id ? { id: option.id } : {}),
									value: option.value?.trim(),
									allow_text: option.allow_text,
									label: option.label?.trim() || '',
									is_updated: !!option.is_updated,
									is_deleted: !!option.is_deleted,
								})) || [],
						};

						if (!isFrontendId(questionId)) {
							questionData.id = Number(questionId);
						}
						return questionData;
					},
				),
			};

			if (!isFrontendId(sectionId)) {
				sectionData.id = Number(sectionId);
			}
			return sectionData;
		},
	);

	return {
		...(stateData.id ? { id: stateData.id } : {}),
		name: stateData.name?.trim(),
		index: Number(stateData.index),
		published: stateData.published,
		is_updated: !!stateData.is_updated,
		is_deleted: !!stateData.is_deleted,
		sections,
	};
};

export const transformAPIResponseToStateStructure = (
	apiData: IQuestionnaireTemplateResponse,
): IQuestionnaireTemplateStateData => {
	const sections: Record<string, ISectionTemplateStateData> = {};

	apiData.sections.forEach((section) => {
		const questions: Record<string, IQuestionData> = {};

		section.questions.forEach((question) => {
			const options: IOptionData[] =
				question.options?.map((option) => ({
					id: option.id,
					value: option.value,
					allow_text: option.allow_text,
					label: option.label,
				})) || [];

			questions[question.id!] = {
				id: question.id,
				text: question.text,
				type: question.type,
				serial_number: question.serial_number,
				is_required: question.is_required,
				options,
			};
		});

		sections[section.id!] = {
			id: section.id,
			name: section.name,
			display_name: section.display_name,
			is_hidden: section.is_hidden,
			questions,
		};
	});

	return {
		id: apiData.id,
		name: apiData.name,
		published: apiData.published,
		index: String(apiData.index),
		sections,
	};
};

export const questionnaireTemplateService = ({
	questionnaireData,
	setQuestionnaireValidation,
}: IQuestionnaireTemplateServiceParams) => {
	const validateRequiredData = () => {
		if (!questionnaireData || !setQuestionnaireValidation) return false;

		const errors: IQuestionnaireTemplateValidation = {};

		if (!questionnaireData.name?.trim()) {
			errors.name = 'Questionnaire title is required';
		}

		if (
			typeof questionnaireData.index === 'string' &&
			questionnaireData.index.trim() === ''
		) {
			errors.index = 'Questionnaire index is required';
		} else if (
			isNaN(Number(questionnaireData.index)) ||
			Number(questionnaireData.index) <= 0
		) {
			errors.index = 'Questionnaire index is invalid';
		}

		for (const [sectionId, section] of Object.entries(
			questionnaireData.sections || {},
		)) {
			const sectionErrors: ISectionTemplateValidation = {};

			if (!section.name?.trim()) {
				sectionErrors.display_name = 'Section title is required';
			}

			const questionsErrors: Record<string, IQuestionTemplateValidation> =
				{};

			for (const [questionId, question] of Object.entries(
				section.questions || {},
			)) {
				const questionErrors: IQuestionTemplateValidation = {};

				if (!question.text?.trim()) {
					questionErrors.text = 'Question title is required';
				}

				if (!question.serial_number?.trim()) {
					questionErrors.serial_number = 'Serial number is required';
				}

				if (!question.type?.trim()) {
					questionErrors.type = 'Question type is required';
				}

				// Validate options for select types
				if (
					question.type === 'single_select' ||
					question.type === 'multi_select'
				) {
					if (!question.options || question.options.length === 0) {
						questionErrors.optionError =
							'At least one option is required';
					} else {
						// Validate each option individually
						const optionErrors = question.options.map((option) => {
							const optErr: IOptionTemplateValidation = {};
							if (!option.value?.trim()) {
								optErr.value = 'Option label is required';
							}
							if (option.allow_text && !option.label?.trim()) {
								optErr.label =
									'Label for text field is required';
							}
							return Object.keys(optErr).length > 0 ? optErr : {};
						});

						const allOptionErrorsEmpty = optionErrors.every(
							(err) => Object.keys(err).length === 0,
						);

						if (!allOptionErrorsEmpty) {
							questionErrors.options = optionErrors;
						}
					}
				}

				if (Object.keys(questionErrors).length > 0) {
					questionsErrors[questionId] = questionErrors;
				}
			}

			if (Object.keys(questionsErrors).length > 0) {
				sectionErrors.questions = questionsErrors;
			}

			if (Object.keys(sectionErrors).length > 0) {
				errors.sections = {
					...(errors.sections || {}),
					[sectionId]: sectionErrors,
				};
			}
		}

		setQuestionnaireValidation(errors);
		return Object.keys(errors).length === 0;
	};

	return { validateRequiredData };
};
